import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, Input, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Setting as SettingType } from "/app/src/models";
import { settingService } from "/app/src/services";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers";
import { handlePromiseError } from "/app/src/helpers/api";

/**
 * Component to display the form for updating the PowerPick version
 */
export default function PowerPickVersion() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // useQuery for getting the PowerPick version setting
  const { data: powerPickVersion } = useQuery({
    queryKey: ["powerPickVersionSetting"],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ name: "powerPickVersion", number: "109" }))
        .then((response) => {
          return response.settings[0];
        });
    },
    initialData: { value: undefined },
    select: (data: SettingType) => {
      return data;
    },
  });

  // useMutation for updating the PowerPick version
  const { mutateAsync: updateVersion } = useMutation({
    mutationFn: (updateVersion: SettingType) => {
      return settingService
        .updateSingle(updateVersion.id, { value: updateVersion.value })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["powerPickVersionSetting"], data.setting);
    },
  });

  // useMutation for creating the PowerPick version
  const { mutateAsync: createVersion } = useMutation({
    mutationFn: (createVersion: SettingType) => {
      return settingService
        .createSingle({
          value: createVersion.value,
          name: "powerPickVersion",
          number: 109,
        })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["powerPickVersionSetting"], data.setting);
    },
  });

  // onSubmit handler for creating/updating the PowerPick version
  const onSubmitVersion = useCallback(
    async (
      {
        value,
      }: {
        value: string;
      },
      {
        setFieldError,
      }: { setFieldError: (field: string, value: string) => void },
    ): Promise<void> => {
      if (powerPickVersion?.id) {
        await updateVersion({ id: powerPickVersion.id, value }).catch(
          (error) => {
            return setFieldError("value", error.errors[0]);
          },
        );
      } else {
        await createVersion({ value }).catch((error) => {
          return setFieldError("value", error.errors[0]);
        });
      }
    },
    [createVersion, updateVersion, powerPickVersion.id],
  );

  /**
   * Update version form
   */
  const updateVersionForm: (
    props: FormikProps<{ value: string | undefined }>,
  ) => JSX.Element = useCallback(
    ({ dirty }) => (
      <Form layout="vertical">
        <Row justify="start" gutter={16}>
          <Col span={8}>
            <h3>{`${t("translation:power_pick_version_setting")}`}</h3>
            <p>{t("translation:power_pick_version_setting_description")}</p>
          </Col>
          <Col span={13}>
            <Form.Item
              className="no-label" // skipcq: JS-0394
              name="value"
            >
              <Input
                name="value"
                size="large"
                placeholder={t(
                  "translation:power_pick_version_setting_placeholder",
                )}
              />
            </Form.Item>
          </Col>
          <Col span={3}>
            <SubmitButton
              className="no-label" // skipcq: JS-0394
              type="primary"
              size="large"
              block
              disabled={!dirty}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [t],
  );

  return (
    <Formik
      component={updateVersionForm}
      enableReinitialize
      initialValues={{
        value: powerPickVersion?.value ?? "",
      }}
      onSubmit={onSubmitVersion}
    />
  );
}
